import * as Sentry from "@sentry/react";
import { Button } from "@/ui-components/ui/button";
import { AlertCircle, RotateCcw } from "lucide-react";
import { ReactNode } from "react";
import { getCurrentTraceId } from "@/lib/monitoring/sentry";

interface SentrySectionBoundaryProps {
  boundaryKey: string;
  title: string;
  description: string;
  children: ReactNode;
  className?: string;
}

const BoundaryFallback = ({
  title,
  description,
  onRetry,
  className,
}: {
  title: string;
  description: string;
  onRetry: () => void;
  className?: string;
}) => {
  const traceId = getCurrentTraceId();

  return (
    <div className={className ?? "flex h-full w-full items-center justify-center p-6"}>
      <div className="w-full max-w-md rounded-2xl border border-red-100 bg-white p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <AlertCircle className="h-6 w-6" />
        </div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-500">{description}</p>
        {traceId ? <p className="mt-3 text-xs text-gray-500">Trace-ID: {traceId}</p> : null}
        <Button type="button" variant="outline" className="mt-5 inline-flex items-center gap-2" onClick={onRetry}>
          <RotateCcw className="h-4 w-4" />
          Bereich neu laden
        </Button>
      </div>
    </div>
  );
};

export const SentrySectionBoundary = ({
  boundaryKey,
  title,
  description,
  children,
  className,
}: SentrySectionBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      showDialog={false}
      beforeCapture={(scope, error, componentStack) => {
        scope.setTag("error_boundary_section", boundaryKey);
        scope.setContext("error_boundary", {
          boundaryKey,
          traceId: getCurrentTraceId(),
          errorName: error instanceof Error ? error.name : "unknown",
          componentStack,
        });
      }}
      fallback={({ resetError }) => (
        <BoundaryFallback
          title={title}
          description={description}
          onRetry={resetError}
          className={className}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
